"use client";

import { useEffect, useRef, useState } from "react";

type Step = "idle" | "open" | "sending" | "done" | "error";

export default function ChatWidget() {
  const [step, setStep] = useState<Step>("idle");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && step === "open") setStep("idle");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  // Focus first input when opened
  useEffect(() => {
    if (step === "open") setTimeout(() => firstInputRef.current?.focus(), 80);
  }, [step]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg("");
    setStep("sending");
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message }),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        throw new Error(data.error ?? "Error al enviar");
      }
      setStep("done");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error al enviar");
      setStep("error");
    }
  }

  function reset() {
    setName(""); setEmail(""); setMessage(""); setErrorMsg("");
    setStep("open");
  }

  return (
    <div className="chat-widget">
      {/* Panel */}
      {step !== "idle" && (
        <div className="chat-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label="Chat de soporte">
          <div className="chat-panel-head">
            <div>
              <div className="chat-panel-title">¿En qué podemos ayudarte?</div>
              <div className="chat-panel-sub">Te respondemos en breve por email.</div>
            </div>
            <button className="chat-close" onClick={() => setStep("idle")} aria-label="Cerrar chat" type="button">✕</button>
          </div>

          <div className="chat-panel-body">
            {(step === "open" || step === "sending" || step === "error") && (
              <form onSubmit={handleSubmit} className="chat-form">
                <label className="chat-label">
                  Nombre
                  <input
                    ref={firstInputRef}
                    className="chat-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Tu nombre"
                  />
                </label>
                <label className="chat-label">
                  Email
                  <input
                    className="chat-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="tu@email.com"
                  />
                </label>
                <label className="chat-label">
                  Mensaje
                  <textarea
                    className="chat-input chat-textarea"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    placeholder="Cuéntanos en qué podemos ayudarte..."
                    rows={4}
                  />
                </label>
                {step === "error" && (
                  <div className="chat-error">{errorMsg}</div>
                )}
                <button className="chat-submit" type="submit" disabled={step === "sending"}>
                  {step === "sending" ? "Enviando..." : "Enviar mensaje"}
                </button>
              </form>
            )}

            {step === "done" && (
              <div className="chat-done">
                <div className="chat-done-icon">✓</div>
                <div className="chat-done-title">¡Mensaje recibido!</div>
                <div className="chat-done-sub">Te responderemos en {email} lo antes posible.</div>
                <button className="chat-submit chat-submit--secondary" type="button" onClick={reset}>
                  Enviar otro mensaje
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bubble button */}
      <button
        className={`chat-bubble${step !== "idle" ? " is-open" : ""}`}
        onClick={() => step === "idle" ? setStep("open") : setStep("idle")}
        aria-label={step === "idle" ? "Abrir chat de soporte" : "Cerrar chat"}
        type="button"
      >
        {step !== "idle" ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>
    </div>
  );
}
